import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Row } from 'react-bootstrap';
import _ from 'lodash';
import camelcase from 'camelcase';
import pluralize from 'pluralize';
import humanizeString from 'humanize-string';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

import SelectFile from './steps/select_file';
import FieldMapper from './steps/field_mapper';
import FieldOverrideMapper from './steps/field_override_mapper';
import EnumFieldMapper from './steps/enum_field_mapper';
import ImportPreview from './steps/import_preview';
import ImportProgress from './steps/import_progress';
import ImportReview from './steps/import_review';

import './styles.scss';

export default class CsvImporter extends React.Component {
  static propTypes = {
    availableDatabases: PropTypes.array,
    currentDatabase: PropTypes.object,
    fieldFormat: PropTypes.object.isRequired,
    fileUploadHelpText: PropTypes.string,
    importPath: PropTypes.string.isRequired,
    objectName: PropTypes.string,
    objectType: PropTypes.string,
    onFinish: PropTypes.func
  }

  state = {
    buildMapping: true,
    currentDatabase: this.props.currentDatabase,
    csv: null,
    csvMapping: {},
    csvRows: [],
    enumMapping: {},
    errors: [],
    fieldOverrideMapping: {},
    fileName: null,
    headerRow: [],
    importJobId: null,
    loading: false,
    stepIndex: 0,
    summary: []
  }

  handleCsvFileSelected = (csv) => {
    this.setState({ csv, csvRows: [], headerRow: [], fileName: null });
  }

  handleCsvFileParsed = (csvRows, headerRow, fileName) => {
    this.setState({ buildMapping: true, csvRows, headerRow, fileName });
  }

  handleUpdateCsvMapping = (importedField, buildoutField) => {
    this.setState((prevState) => {
      const { [importedField]: removedBuildoutField, ...csvMapping } = prevState.csvMapping;
      csvMapping[importedField] = buildoutField;

      const enumMapping = { ...prevState.enumMapping };

      if (this.updateFieldInEnumMapping(removedBuildoutField)) delete enumMapping[removedBuildoutField];
      if (this.updateFieldInEnumMapping(buildoutField)) {
        enumMapping[buildoutField] = this.detectEnumMapping(csvMapping, importedField)[buildoutField];
      }

      return { csvMapping, enumMapping };
    });
  }

  updateFieldInEnumMapping = removedBuildoutField => (
    removedBuildoutField != null && this.props.fieldFormat[removedBuildoutField].type === 'enum'
  )

  handleUpdateEnumMapping = (buildoutField, importedValue, buildoutValue) => {
    this.setState(({ enumMapping }) => ({
      enumMapping: {
        ...enumMapping,
        [buildoutField]: { ...enumMapping[buildoutField], [importedValue]: buildoutValue }
      }
    }));
  }

  handleUpdateOverrideMapping = (buildoutField, value) => {
    this.setState(({ fieldOverrideMapping }) => (
      { fieldOverrideMapping: { ...fieldOverrideMapping, [buildoutField]: value } }
    ));
  }

  handleDatabaseSelect = (database) => {
    this.setState({ currentDatabase: database });
  }

  goToNextStep = () => {
    if (this.state.stepIndex === 2) {
      this.startImport();
    } else {
      this.setState(({ buildMapping, stepIndex }) => {
        if (stepIndex >= 4) return {};

        const nextState = { stepIndex: stepIndex + 1 };

        if (nextState.stepIndex === 1 && buildMapping) {
          nextState.csvMapping = this.detectCsvMapping();
          nextState.enumMapping = this.detectEnumMapping(nextState.csvMapping);
          nextState.fieldOverrideMapping = _.mapValues(this.overrideFields(nextState.csvMapping), () => null);
          nextState.buildMapping = false;
        }

        return nextState;
      });
    }
  }

  goToPrevStep = () => {
    this.setState(({ stepIndex }) => ({ errors: [], stepIndex: stepIndex - 1 }));
  }

  startImport = () => {
    const { importPath, objectType } = this.props;
    const { csv, csvMapping, currentDatabase, enumMapping, fieldOverrideMapping } = this.state;

    const formData = new FormData();
    formData.set('csv_mapping', JSON.stringify(csvMapping));
    formData.set('enum_mapping', JSON.stringify(enumMapping));
    formData.set('field_mapping', JSON.stringify(fieldOverrideMapping));
    if (currentDatabase) {
      formData.set('current_database_id', currentDatabase.value);
    }
    formData.append(`${objectType}[csv]`, csv);
    this.setState({ loading: true });

    axios.post(importPath, formData).then(({ data }) => {
      this.setState(({ stepIndex }) => ({ importJobId: data.import_job_id, loading: false, stepIndex: stepIndex + 1 }));
    }).catch(({ response }) => {
      const nextState = { loading: false };

      if (response.data.errors) nextState.errors = response.data.errors;

      this.setState(nextState);
    });
  }

  finishImport = (summary) => {
    this.setState(({ stepIndex }) => ({ stepIndex: stepIndex + 1, summary }));
  }

  handleCancel = () => {
    this.props.onFinish();
  }

  handleDone = () => {
    this.setState({ loading: true });
    this.props.onFinish(this.state.importJobId);
  }

  detectCsvMapping() {
    const mapping = {};

    const mappableFields = this.mappingFields();

    this.state.headerRow.forEach((importedField) => {
      const buildoutField = _.findKey(mappableFields, (field, fieldKey) => {
        return this.compare(fieldKey, importedField) ||
          (field.label && importedField && this.compare(field.label, importedField)) ||
          (field.aliases || []).some(alias => this.compare(alias, importedField));
      });

      mapping[importedField] = buildoutField || null;
    });

    return mapping;
  }

  detectEnumMapping = (csvMapping, specifiedField = null) => {
    const mapping = {};

    this.state.headerRow.forEach((importedField, columnIndex) => {
      const buildoutField = csvMapping[importedField];
      if (buildoutField == null) return;

      const { type, values } = this.props.fieldFormat[buildoutField];
      const mapField = specifiedField == null || specifiedField === importedField;

      if (type === 'enum' && mapField) mapping[buildoutField] = this.detectEnumField(values, columnIndex);
    });

    return mapping;
  }

  detectEnumField = (enumValues, columnIndex) => {
    const csvMapping = {};
    const detectedValues = new Set(this.state.csvRows.map(row => row[columnIndex]).filter(field => field.length > 0));

    detectedValues.forEach((detectedValue) => {
      const buildoutValue = _.findKey(enumValues, (value, enumKey) => (
        this.compare(value.toString(), detectedValue) || this.compare(enumKey, detectedValue)
      ));

      csvMapping[detectedValue] = buildoutValue || null;
    });

    return csvMapping;
  }

  compare = (value1, value2) => this.normalize(value1) === this.normalize(value2)

  normalize = string => humanizeString(string).toLowerCase()

  objectName = () => humanizeString(this.props.objectType)

  mappedFields = mapping => _.filter(mapping, field => field != null)

  columnLabels = mapping => _.map(this.mappedFields(mapping), field => this.props.fieldFormat[field].label || field)

  mappingFields = () => _.pickBy(this.props.fieldFormat, field => field.type !== 'override')
  overrideFields = () => _.pickBy(this.props.fieldFormat, field => field.type === 'override')

  stepName = () => {
    switch (this.state.stepIndex) {
    case 0:
      return 'Select File';
    case 1:
      return 'Map Fields';
    case 2:
      return 'Preview Import';
    case 3:
      return 'Importing...';
    case 4:
      return 'Import Review';
    }
  }

  valid = () => {
    const { availableDatabases } = this.props;
    const { csvMapping, csvRows, currentDatabase, enumMapping, headerRow, loading, stepIndex } = this.state;

    if (loading) return false;

    switch (stepIndex) {
    case 0:
      return csvRows.length > 0 && headerRow.length > 0 && (availableDatabases == null ? true : currentDatabase != null);
    case 1: {
      const importedFields = this.mappedFields(csvMapping);
      return importedFields.length !== 0 &&
        importedFields.every(field => importedFields.indexOf(field) === importedFields.lastIndexOf(field)) &&
        _.every(enumMapping, mapping => this.mappedFields(mapping).length !== 0);
    }
    case 2:
      return true;
    }
  }

  renderStep() {
    const { availableDatabases, fieldFormat, objectName } = this.props;
    const {
      currentDatabase,
      csvRows,
      csvMapping,
      enumMapping,
      errors,
      fieldOverrideMapping,
      fileName,
      headerRow,
      importJobId,
      stepIndex,
      summary
    } = this.state;

    switch (stepIndex) {
    case 0:
      return (
        <SelectFile
          availableDatabases={availableDatabases}
          csvRows={csvRows}
          currentDatabase={currentDatabase}
          fileName={fileName}
          fileUploadHelpText={this.props.fileUploadHelpText}
          headerRow={headerRow}
          objectName={objectName}
          onCsvFileParsed={this.handleCsvFileParsed}
          onCsvFileSelected={this.handleCsvFileSelected}
          onDatabaseSelect={this.handleDatabaseSelect}
        />
      );
    case 1:
      return (
        <React.Fragment>
          <FieldMapper
            csvMapping={csvMapping}
            csvRows={csvRows}
            fieldFormat={this.mappingFields()}
            fileName={fileName}
            headerRow={headerRow}
            onUpdateCsvMapping={this.handleUpdateCsvMapping}
          />
          {!_.isEmpty(fieldOverrideMapping) && (
            <FieldOverrideMapper
              fieldOverrideMapping={fieldOverrideMapping}
              overrideFields={this.overrideFields()}
              pluralizedObjectName={pluralize(this.objectName())}
              onUpdateOverrideMapping={this.handleUpdateOverrideMapping}
            />
          )}
          {!_.isEmpty(enumMapping) && (
            <Row>
              {_.map(enumMapping, (mapping, buildoutField) => {
                return (
                  <EnumFieldMapper
                    buildoutField={buildoutField}
                    buildoutFieldLabel={fieldFormat[buildoutField].label || buildoutField}
                    enumMapping={mapping}
                    enumValues={fieldFormat[buildoutField].values}
                    key={buildoutField}
                    onUpdateEnumMapping={this.handleUpdateEnumMapping}
                  />
                );
              }) }
            </Row>
          )}
        </React.Fragment>
      );
    case 2:
      return (
        <ImportPreview
          fieldColumnLabels={this.columnLabels(csvMapping)}
          errors={errors}
          fileName={fileName}
          objectName={this.objectName()}
          rowCount={csvRows.length}
        />
      );
    case 3:
      return (
        <ImportProgress
          fileName={fileName}
          importJobId={importJobId}
          objectName={pluralize(this.objectName())}
          onComplete={this.finishImport}
        />
      );
    case 4: {
      const overrideFields = _.keys(_.pickBy(fieldOverrideMapping, value => value != null));

      return (
        <ImportReview
          enumColumnLabels={overrideFields.map(humanizeString)}
          enumColumns={overrideFields.map(camelcase)}
          fieldColumnLabels={this.columnLabels(csvMapping)}
          fieldColumns={this.mappedFields(csvMapping).map(camelcase)}
          headerRow={headerRow.map(camelcase)}
          headerRowLabels={headerRow}
          importJobId={importJobId}
          summary={summary}
        />
      );
    }
    }
  }

  renderButtons() {
    const { loading, stepIndex } = this.state;
    const { onFinish } = this.props;

    if (stepIndex === 3) return;

    return (
      <div className="text-right">
        {stepIndex > 0 && stepIndex < 3 && (
          <div className="px-5 btn muted-button" name="back" onClick={this.goToPrevStep}>
            <span>&lt; Back</span>
          </div>
        )}
        {onFinish && stepIndex < 3 && (
          <button className="px-5 btn btn-cancel mr-4" name="cancel" onClick={onFinish}>Cancel</button>
        )}
        {stepIndex < 3 && (
          <button className="px-5 btn btn-save" disabled={!this.valid()} name="next" onClick={this.goToNextStep}>
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <React.Fragment>{stepIndex < 2 ? 'Next' : 'Import'}</React.Fragment>
            )}
          </button>
        )}
        {onFinish && stepIndex > 3 && (
          <button className="px-5 btn btn-save" disabled={loading} name="done" onClick={this.handleDone}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Done'}
          </button>
        )}
      </div>
    );
  }

  render() {
    const { stepIndex } = this.state;
    return (
      <div className="csv-importer">
        <h3 className="mt-0">
          {stepIndex < 3 && <strong className="mr-3 text-uppercase">Step {stepIndex + 1} of 3</strong>}
          <span>{this.stepName()}</span>
        </h3>
        {this.renderStep()}
        {this.renderButtons()}
      </div>
    );
  }
}
